import React, { ReactElement, useState } from 'react';
import { NavigateFunction, useNavigate, useParams } from 'react-router-dom';
import ReactTooltip from 'react-tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AddCircle, Edit, ImportExport, StackedLineChart } from '@mui/icons-material';
import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { LoadingButton } from '@mui/lab';
import { Box, Button, Tooltip } from '@mui/material';
import clsx from 'clsx';
import { User } from 'types/User';

import { ROUTE_HISTORY_KEY } from '../AppConstants';
import { isInternalCustomer } from '../helpers/AccessManagementHelpers';
import { removeUnderscoreToCapitalize } from '../helpers/FormattingHelpers';
import { isNullable } from '../helpers/TypingHelpers';
import { GroupType } from '../services/scan-types/schemas';
import { ScanActionItem, ScanActionType, ScanBoxCommon, ScanOrBoxStatus } from '../types/Scan';
import {
    doesScanBelongToUserLocation,
    isAnotherUserScanning,
    isUserRemoteViewer
} from '../views/ScanOrBoxDetail/ScanOrBoxDetail.utils';
import ContextMenu from './ContextMenu';

import '../styles/components/ScanOrBoxInfoHeader.less';

export type ButtonState = { isDisabled: boolean; isLoading?: boolean; isVisible?: boolean; tooltipMsg?: string };

/**
 * Component for displaying details of either a scan or a box (`scanItem`), with support for shared logic
 * across both cases. It is used in the scan detail view, box detail view, and box overview.
 *
 * The `scanItem` represents either a scan or a box, both of which follow the `ScanBoxCommon` structure.
 * However, box models do not include certain scan-level fields (like destination name, tracking ID,
 * or sales order ID). These values are extracted from the original scan response and passed separately
 * via `scanItemMeta` to provide the necessary context when rendering box details.
 */

interface Props {
    scanItem: ScanBoxCommon;
    scanItemMeta: {
        scanId: number;
        destinationName?: string;
        trackingId?: string;
        salesOrderId?: string;
    };
    currentUser: User;
    buttonsState: Record<'COMPLETE' | 'ACTIONS', ButtonState>;
    onAction: (type: ScanActionType) => void;
    isBoxOverview?: boolean;
}

const CopyIcon = ({ onCopy }: { onCopy: () => Promise<void> }) => {
    const [copied, setCopied] = useState(false);

    const handleClick = async () => {
        if (copied) return;

        try {
            await onCopy();
            setCopied(true);
            setTimeout(() => setCopied(false), 1200);
        } catch (error) {
            console.error('Failed to copy:', error);
        }
    };

    return (
        <button
            type="button"
            aria-label="Copy"
            title="Copy ID"
            className="no-style"
            onClick={handleClick}
            disabled={copied}
        >
            {copied ? (
                <Tooltip
                    title="Copied!"
                    open={copied}
                    placement="top"
                    slotProps={{
                        tooltip: {
                            sx: {
                                fontSize: 10
                            }
                        }
                    }}
                >
                    <CheckIcon sx={{ fontSize: 18 }} className="success-text" />
                </Tooltip>
            ) : (
                <ContentCopyIcon sx={{ fontSize: 18 }} />
            )}
        </button>
    );
};

const ScanOrBoxInfoHeader = (props: Props): ReactElement => {
    const { scanItem, currentUser, onAction, buttonsState, isBoxOverview = false, scanItemMeta } = props;

    const navigate = useNavigate();

    const { boxId } = useParams();
    const isViewingBox = Boolean(boxId && scanItemMeta.scanId);

    const scanType = scanItem?.scanTypeName;
    const formattedScanType = removeUnderscoreToCapitalize(scanType, true);

    const scanStatus = scanItem?.statusName;
    const isScanInProgress = scanStatus === 'IN_PROGRESS';
    const isScanComplete = scanStatus === 'COMPLETE';

    const anotherUserWarning = isAnotherUserScanning(scanItem, currentUser);
    const isRemoteViewer = isUserRemoteViewer(currentUser);
    const scanBelongsToUserLocation = doesScanBelongToUserLocation(scanItem, currentUser);
    const isInternalUser = isInternalCustomer(currentUser);
    const actionItems = getScanActionItems({
        isBoxOverview: isBoxOverview,
        isControlantUser: isInternalUser,
        anotherUserWarning: Boolean(anotherUserWarning),
        scanStatus: scanStatus
    });

    return (
        <div className="detail-container" data-testid="detail-container">
            <div className="upper-section">
                <div data-testid="headline" className="flex-baseline">
                    <FontAwesomeIcon
                        icon="circle"
                        className={clsx('scan-status', {
                            blue: isScanInProgress,
                            green: isScanComplete
                        })}
                    />

                    <h1 id="no-margin">
                        {formattedScanType} {!isViewingBox ? 'Scan' : ''}
                    </h1>

                    <div className="extra-info">
                        · ID {scanItem.id ?? 'N/A'}
                        <CopyIcon onCopy={() => navigator.clipboard.writeText(scanItem.id?.toString() ?? '')} />·{' '}
                        {scanStatus ? removeUnderscoreToCapitalize(scanStatus, true) : 'N/A'}
                    </div>
                </div>

                <Box display="flex" flexDirection="row" gap="16px" alignItems="flex-end">
                    <Button
                        variant="contained"
                        onClick={() => handleNavigateBackOrFallback(navigate, isViewingBox, scanItemMeta.scanId)}
                    >
                        Back
                    </Button>

                    {buttonsState.ACTIONS.isVisible && (
                        <ContextMenu
                            items={actionItems}
                            onAction={onAction}
                            buttonState={buttonsState.ACTIONS}
                            align="right"
                        />
                    )}

                    {buttonsState.COMPLETE.isVisible && (
                        <>
                            <ReactTooltip type="dark" effect="solid" id="complete-btn" place="bottom" />
                            <span data-tip={buttonsState.COMPLETE.tooltipMsg} data-for="complete-btn">
                                <LoadingButton
                                    variant="contained"
                                    color="success"
                                    loading={buttonsState.COMPLETE.isLoading}
                                    disabled={buttonsState.COMPLETE.isDisabled}
                                    onClick={() => onAction(ScanActionType.COMPLETE)}
                                    startIcon={<CheckIcon />}
                                >
                                    {boxId ? 'Complete Box' : 'Complete Scan'}
                                </LoadingButton>
                            </span>
                        </>
                    )}
                </Box>
            </div>

            <div data-testid="lower-section" className="lower-section">
                <div>
                    <div className="flex-row">
                        <FontAwesomeIcon icon="user" className="margin-right" />
                        <span id="test-created-by">Created By: {scanItem.createdByUsername ?? 'N/A'}</span>
                    </div>
                    {scanItem?.scanTypeGroupType === GroupType.OUTBOUND && (
                        <div data-testid="destination-field">
                            <FontAwesomeIcon icon="map-marker-check" className="margin-right" />
                            Destination:{' '}
                            {scanItemMeta.destinationName
                                ? removeUnderscoreToCapitalize(scanItemMeta.destinationName, true)
                                : 'N/A'}
                        </div>
                    )}
                    {isRemoteViewer && !scanBelongsToUserLocation && (
                        <div data-testid="source-location-field">
                            <FontAwesomeIcon icon="map-marker-check" className="margin-right" />
                            Source location: {scanItem?.locationName ? scanItem.locationName : 'N/A'}
                        </div>
                    )}
                </div>

                <div>
                    <div>
                        <FontAwesomeIcon icon="truck" className="margin-right" />
                        {scanType === 'MANUFACTURER-BOX' ? 'Batch number: ' : 'Tracking ID: '}{' '}
                        {scanItemMeta?.trackingId ?? 'N/A'}
                    </div>
                    <div>
                        <FontAwesomeIcon icon="receipt" />{' '}
                        {scanType === 'MANUFACTURER-BOX' ? 'Shipping number: ' : 'Sales Order ID: '}
                        {scanItemMeta?.salesOrderId ?? 'N/A'}
                    </div>
                </div>
            </div>
        </div>
    );
};

export const handleNavigateBackOrFallback = (navigate: NavigateFunction, isViewingBox: boolean, scanId: number) => {
    // We get the app route history from sessionStorage so we can determine if there's a previous in-app route to go back to
    const history: string[] = JSON.parse(sessionStorage.getItem(ROUTE_HISTORY_KEY) ?? '[]');

    if (history.length > 1) {
        // There's an in-app previous route → go back
        navigate(-1);
    } else if (isViewingBox) {
        // When viewing a box → go to the parent box scan
        navigate(`/scanning/${scanId}`);
    } else {
        // Otherwise → safe fallback
        navigate('/scanning');
    }
};

interface GetScanActionItemsParams {
    isBoxOverview: boolean;
    isControlantUser: boolean;
    anotherUserWarning: boolean;
    scanStatus: ScanOrBoxStatus | undefined;
}

/**
 * Returns scan action items based on the view and scan state.
 *
 * @param params - Configuration object
 * @param params.isBoxOverview - Whether the Box Overview is shown.
 * @param params.isControlantUser - Whether the user is an internal Controlant user.
 * @param params.anotherUserWarning - Whether another user is scanning.
 * @param params.scanStatus - Current scan or box status.
 *
 * @returns List of actions to display.
 *
 * - Always includes "Edit Scan"
 * - Adds "Add Box" if in Box Overview and not COMPLETE
 * - Adds "Import Loggers" and "Clear All Loggers" if in Scan view, not COMPLETE, and no other user warning
 */
export const getScanActionItems = ({
    isBoxOverview,
    isControlantUser,
    anotherUserWarning,
    scanStatus
}: GetScanActionItemsParams): Array<ScanActionItem> => {
    const actionsData = [
        { type: ScanActionType.EDIT, text: 'Edit Scan', icon: Edit },
        ...(!isBoxOverview
            ? [{ type: ScanActionType.STATISTICS, text: 'Generate Statistics', icon: StackedLineChart }]
            : [])
    ];

    if (isNullable(scanStatus)) {
        return actionsData;
    }

    if (isBoxOverview && scanStatus !== 'COMPLETE') {
        actionsData.push({ type: ScanActionType.ADD, text: 'Add Box', icon: AddCircle });
    }

    const canUserModify = !anotherUserWarning && !isBoxOverview && scanStatus !== 'COMPLETE';

    if (canUserModify && isControlantUser) {
        actionsData.push({ type: ScanActionType.IMPORT, text: 'Import Loggers', icon: ImportExport });
    }

    if (canUserModify) {
        actionsData.push({ type: ScanActionType.CLEAR, text: 'Clear All Loggers', icon: ClearIcon });
    }
    return actionsData;
};

export default ScanOrBoxInfoHeader;
